import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import ImageUploader from "@/components/admin/ImageUploader";

export default function AdminHomepage() {
  const [form, setForm] = useState({
    hero_title: "", hero_subtitle: "", hero_cta: "", hero_image_url: "",
    about_title: "", about_body: "", stats: {}, academy_cards: [], contact: {},
  });
  const [statsInput, setStatsInput] = useState({ athletes: "", coaches: "", trophies: "", hours_booked_month: "" });
  const [contact, setContact] = useState({
    address: "", phone: "", email: "",
    map_embed_url: "", google_business_url: "", google_maps_url: "",
    socials: { instagram: "", facebook: "", youtube: "", whatsapp: "", twitter: "", linkedin: "" },
  });
  const [cards, setCards] = useState([]);

  useEffect(() => {
    api.get("/homepage").then((r) => {
      const d = r.data || {};
      setForm(d);
      setStatsInput({
        athletes: d.stats?.athletes || "",
        coaches: d.stats?.coaches || "",
        trophies: d.stats?.trophies || "",
        hours_booked_month: d.stats?.hours_booked_month || "",
      });
      const dc = d.contact || {};
      setContact({
        address: dc.address || "",
        phone: dc.phone || "",
        email: dc.email || "",
        map_embed_url: dc.map_embed_url || "",
        google_business_url: dc.google_business_url || "",
        google_maps_url: dc.google_maps_url || "",
        socials: {
          instagram: dc.socials?.instagram || "",
          facebook: dc.socials?.facebook || "",
          youtube: dc.socials?.youtube || "",
          whatsapp: dc.socials?.whatsapp || "",
          twitter: dc.socials?.twitter || "",
          linkedin: dc.socials?.linkedin || "",
        },
      });
      setCards(d.academy_cards || []);
    });
  }, []);

  const setCard = (i, k, v) => {
    const c = [...cards]; c[i] = { ...c[i], [k]: v }; setCards(c);
  };

  const save = async () => {
    try {
      const stats = {};
      Object.entries(statsInput).forEach(([k, v]) => { if (v !== "") stats[k] = parseInt(v) || 0; });
      await api.post("/admin/homepage", { ...form, stats, contact, academy_cards: cards });
      toast.success("Homepage saved");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="admin-homepage">
      <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-3">// CMS · Homepage & Contact</div>
      <h1 className="font-display text-4xl font-black uppercase tracking-tighter leading-none mb-8">Homepage.</h1>

      <div className="max-w-4xl bg-[#131A2B] border border-white/12 p-8 space-y-6">
        <Section title="Hero">
          <Field label="Hero Title">
            <textarea value={form.hero_title || ""} onChange={(e) => setForm({ ...form, hero_title: e.target.value })} rows={2} data-testid="hp-hero-title" className="w-full bg-transparent border border-white/20 py-3 px-4" />
          </Field>
          <Field label="Hero Subtitle">
            <textarea value={form.hero_subtitle || ""} onChange={(e) => setForm({ ...form, hero_subtitle: e.target.value })} rows={2} data-testid="hp-hero-sub" className="w-full bg-transparent border border-white/20 py-3 px-4" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Hero CTA Label">
              <input value={form.hero_cta || ""} onChange={(e) => setForm({ ...form, hero_cta: e.target.value })} className="w-full bg-transparent border border-white/20 py-3 px-4" />
            </Field>
            <Field label="Hero Background Image">
              <ImageUploader
                value={form.hero_image_url || ""}
                testid="hp-hero-image-upload"
                label={form.hero_image_url ? "Replace hero image" : "Upload hero background"}
                onUploaded={(url) => setForm({ ...form, hero_image_url: url })}
                onCleared={() => setForm({ ...form, hero_image_url: "" })}
              />
              <input
                value={form.hero_image_url || ""}
                onChange={(e) => setForm({ ...form, hero_image_url: e.target.value })}
                data-testid="hp-hero-image"
                placeholder="…or paste an image URL"
                className="w-full bg-transparent border border-white/20 py-2 px-4 mt-2 text-xs font-mono"
              />
            </Field>
          </div>
          {form.hero_image_url && (
            <img src={form.hero_image_url} alt="preview" className="mt-2 w-full h-40 object-cover border border-white/12" data-testid="hp-hero-preview" />
          )}
        </Section>

        <Section title="Hero Portrait (Head Coach / Featured Person)">
          <p className="text-xs text-white/50 -mt-2 mb-2">Square photo shown on the top-right of the hero. Best size: 800×800 or larger. Leave empty to hide.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Field label="Portrait Image">
                <ImageUploader
                  value={form.hero_portrait_url || ""}
                  testid="hp-portrait-upload"
                  label={form.hero_portrait_url ? "Replace portrait" : "Upload square portrait"}
                  onUploaded={(url) => setForm({ ...form, hero_portrait_url: url })}
                  onCleared={() => setForm({ ...form, hero_portrait_url: "" })}
                />
                <input
                  value={form.hero_portrait_url || ""}
                  onChange={(e) => setForm({ ...form, hero_portrait_url: e.target.value })}
                  data-testid="hp-portrait-url"
                  placeholder="…or paste an image URL"
                  className="w-full bg-transparent border border-white/20 py-2 px-4 mt-2 text-xs font-mono"
                />
              </Field>
              {form.hero_portrait_url && (
                <img src={form.hero_portrait_url} alt="portrait preview" className="mt-2 w-40 h-40 object-cover border border-white/12" data-testid="hp-portrait-preview" />
              )}
            </div>
            <div className="space-y-3">
              <Field label="Name (shown on portrait)">
                <input
                  value={form.hero_portrait_name || ""}
                  onChange={(e) => setForm({ ...form, hero_portrait_name: e.target.value })}
                  placeholder="e.g. Kapil Bhatt"
                  data-testid="hp-portrait-name"
                  className="w-full bg-transparent border border-white/20 py-3 px-4"
                />
              </Field>
              <Field label="Role / Caption">
                <input
                  value={form.hero_portrait_role || ""}
                  onChange={(e) => setForm({ ...form, hero_portrait_role: e.target.value })}
                  placeholder="e.g. Head Coach · Cricket"
                  data-testid="hp-portrait-role"
                  className="w-full bg-transparent border border-white/20 py-3 px-4"
                />
              </Field>
            </div>
          </div>
        </Section>

        <Section title="About / Stats">
          <div className="grid grid-cols-2 gap-3">
            <Field label="About Title"><input value={form.about_title || ""} onChange={(e) => setForm({ ...form, about_title: e.target.value })} className="w-full bg-transparent border border-white/20 py-3 px-4" /></Field>
          </div>
          <Field label="About Body">
            <textarea value={form.about_body || ""} onChange={(e) => setForm({ ...form, about_body: e.target.value })} rows={4} className="w-full bg-transparent border border-white/20 py-3 px-4" />
          </Field>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
            {["athletes", "coaches", "trophies", "hours_booked_month"].map((k) => (
              <div key={k}>
                <div className="text-[10px] font-mono uppercase tracking-widest text-white/40 mb-1">{k.replace("_", " ")}</div>
                <input type="number" value={statsInput[k]} onChange={(e) => setStatsInput({ ...statsInput, [k]: e.target.value })} className="w-full bg-transparent border border-white/20 py-2 px-3 text-sm" />
              </div>
            ))}
          </div>
        </Section>

        <Section title="Academy Cards (shown on homepage)">
          {cards.map((c, i) => (
            <div key={i} className="border border-white/12 p-4 mb-3 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid={`academy-card-${i}`}>
              <input value={c.name || ""} onChange={(e) => setCard(i, "name", e.target.value)} placeholder="Name (e.g. Cricket Academy)" className="bg-transparent border border-white/20 py-2 px-3" />
              <input value={c.image_url || ""} onChange={(e) => setCard(i, "image_url", e.target.value)} placeholder="Image URL" className="bg-transparent border border-white/20 py-2 px-3" />
              <input value={c.coach || ""} onChange={(e) => setCard(i, "coach", e.target.value)} placeholder="Coach name" className="bg-transparent border border-white/20 py-2 px-3" />
              <input value={c.fee || ""} onChange={(e) => setCard(i, "fee", parseFloat(e.target.value) || 0)} placeholder="Monthly fee" type="number" className="bg-transparent border border-white/20 py-2 px-3" />
              <input value={c.age_groups || ""} onChange={(e) => setCard(i, "age_groups", e.target.value)} placeholder="Age groups (e.g. U-10 · U-14 · U-19)" className="bg-transparent border border-white/20 py-2 px-3 md:col-span-2" />
              {c.image_url && <img src={c.image_url} alt="" className="h-24 object-cover md:col-span-2" />}
            </div>
          ))}
        </Section>

        <Section title="Contact & Footer">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Address"><input value={contact.address} onChange={(e) => setContact({ ...contact, address: e.target.value })} data-testid="hp-address" className="w-full bg-transparent border border-white/20 py-3 px-4" /></Field>
            <Field label="Phone"><input value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} data-testid="hp-phone" className="w-full bg-transparent border border-white/20 py-3 px-4" /></Field>
            <Field label="Email"><input value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} data-testid="hp-email" className="w-full bg-transparent border border-white/20 py-3 px-4 md:col-span-2" /></Field>
          </div>
        </Section>

        <Section title="Google Integration">
          <p className="text-xs text-white/50 -mt-2 mb-2">Paste your Google Maps embed src URL (Google Maps → Share → Embed a map → copy the value of <code className="text-brand-orange">src=</code> attribute). Also paste your Google Business page URL for the &quot;Leave a review&quot; CTA.</p>
          <Field label="Google Maps Embed URL">
            <input value={contact.map_embed_url} onChange={(e) => setContact({ ...contact, map_embed_url: e.target.value })} data-testid="hp-maps" placeholder="https://www.google.com/maps/embed?pb=..." className="w-full bg-transparent border border-white/20 py-3 px-4 font-mono text-xs" />
          </Field>
          {contact.map_embed_url && (
            <iframe title="Map preview" src={contact.map_embed_url} className="w-full h-56 border border-white/12 mt-2" loading="lazy" allowFullScreen referrerPolicy="no-referrer-when-downgrade" />
          )}
          <Field label="Google Business / My Business URL (for reviews CTA)">
            <input value={contact.google_business_url} onChange={(e) => setContact({ ...contact, google_business_url: e.target.value })} data-testid="hp-gbusiness" placeholder="https://g.page/... or https://maps.app.goo.gl/..." className="w-full bg-transparent border border-white/20 py-3 px-4 font-mono text-xs" />
          </Field>
          <Field label="Directions link (Google Maps deep link — leave blank to auto-generate from address)">
            <input value={contact.google_maps_url} onChange={(e) => setContact({ ...contact, google_maps_url: e.target.value })} data-testid="hp-directions" placeholder="https://maps.app.goo.gl/... or https://www.google.com/maps/place/..." className="w-full bg-transparent border border-white/20 py-3 px-4 font-mono text-xs" />
          </Field>
        </Section>

        <Section title="Social Media Links">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              { k: "instagram", label: "Instagram URL" },
              { k: "facebook", label: "Facebook URL" },
              { k: "youtube", label: "YouTube URL" },
              { k: "twitter", label: "Twitter / X URL" },
              { k: "linkedin", label: "LinkedIn URL" },
              { k: "whatsapp", label: "WhatsApp (wa.me/…)" },
            ].map((s) => (
              <Field key={s.k} label={s.label}>
                <input
                  value={contact.socials[s.k] || ""}
                  onChange={(e) => setContact({ ...contact, socials: { ...contact.socials, [s.k]: e.target.value } })}
                  data-testid={`hp-social-${s.k}`}
                  placeholder={`https://…`}
                  className="w-full bg-transparent border border-white/20 py-3 px-4 font-mono text-xs"
                />
              </Field>
            ))}
          </div>
        </Section>

        <button onClick={save} data-testid="hp-save" className="w-full bg-brand-blue text-white py-3 font-bold uppercase tracking-widest hover:glow-blue">Save Homepage</button>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="border-b border-white/12 pb-6">
      <div className="font-display text-lg uppercase font-bold brand-orange mb-4">{title}</div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}
function Field({ label, children }) {
  return (
    <div>
      <div className="font-mono text-xs uppercase tracking-widest text-white/40 mb-1">{label}</div>
      {children}
    </div>
  );
}
